import React, {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";

const MOBILE_QUERY = "(max-width: 639px)";

const useIsMobile = () => {
    const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(MOBILE_QUERY).matches;
    const [isMobile, setIsMobile] = useState(get);
    useEffect(() => {
        if (typeof window.matchMedia !== "function") return undefined;
        const query = window.matchMedia(MOBILE_QUERY);
        const onChange = () => setIsMobile(query.matches);
        query.addEventListener?.("change", onChange);
        return () => query.removeEventListener?.("change", onChange);
    }, []);
    return isMobile;
};

/**
 * Header menu: dropdown under its trigger from `sm` up, bottom sheet on phones
 * (portaled to body — the page-transition transform and the sticky header
 * would otherwise clip/offset it). Handles outside click and Escape.
 * `trigger` is rendered inside the anchor; `children` are the menu rows.
 */
const MenuSheet = ({isOpen, onClose, title, trigger, children, isDarkMode = false, testId}) => {
    const {t} = useTranslation("activite");
    const isMobile = useIsMobile();
    const anchorRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        const inside = (target) =>
            anchorRef.current?.contains(target) || panelRef.current?.contains(target);
        const handlePointer = (event) => { if (!inside(event.target)) onClose(); };
        const handleKeyDown = (event) => { if (event.key === "Escape") onClose(); };
        document.addEventListener("mousedown", handlePointer);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointer);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    // Lock the page behind the sheet
    useEffect(() => {
        if (!isOpen || !isMobile) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = previous; };
    }, [isOpen, isMobile]);

    const header = (
        <div className={`px-4 py-3 border-b ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"} ${isMobile ? "" : "sticky top-0"}`}>
            <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{title}</p>
        </div>
    );

    return (
        <div ref={anchorRef} className="relative" data-testid={testId}>
            {trigger}
            {isOpen && !isMobile && (
                <div ref={panelRef} role="menu"
                     className={`absolute right-0 mt-2 w-[280px] max-h-[70vh] overflow-y-auto rounded-xl shadow-2xl border-2 z-50 ${
                         isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                     }`}>
                    {header}
                    {children}
                </div>
            )}
            {isOpen && isMobile && createPortal(
                <div className="fixed inset-0 z-[80] flex items-end">
                    <div className="absolute inset-0 bg-black/40" aria-hidden="true"/>
                    <div ref={panelRef} role="menu"
                         className={`relative w-full max-h-[80vh] flex flex-col rounded-t-2xl shadow-2xl overflow-hidden animate-[sheetUp_.2s_ease-out] ${
                             isDarkMode ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
                         }`}
                         style={{paddingBottom: "env(safe-area-inset-bottom)"}}>
                        <div className="flex justify-center pt-2 pb-1">
                            <span className={`w-10 h-1 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}/>
                        </div>
                        {header}
                        <div className="overflow-y-auto">{children}</div>
                        <button type="button" onClick={onClose}
                                className={`w-full py-4 font-medium text-primary border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                            {t("journaux.close")}
                        </button>
                    </div>
                    <style>{"@keyframes sheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}"}</style>
                </div>,
                document.body
            )}
        </div>
    );
};

export default MenuSheet;
